import { Injectable } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TicketClientApiService } from 'src/app/core/service/apis/tickets/ticket-client-api.service';
import { TicketMapperService } from 'src/app/shared/service/ticket-client/tickets-mapper.service';
import { ActorType, Ticket, TicketCategory, TicketGetResponse } from 'src/app/shared/models/tickets/ticket.model';
import { TicketClientRepository } from '../../domain/repository/ticket-client.repository';
import { TicketClientRegisterPayload } from '../../domain/models/ticket-client.model';
import { DocumentApiService } from 'src/app/core/service/apis/files/document-api.service';
import { DOMAIN_TYPE_SUPPORT } from 'src/app/shared/models/documents/documents.model';
import { AttachmentType } from 'src/app/shared/constants/attachment-type.constants';
import { TicketFieldUpdateService } from 'src/app/shared/service/ticket-client/ticket-field-update.service';

@Injectable()
export class HttpTicketClientRepository implements TicketClientRepository {
  constructor(
    private readonly api: TicketClientApiService,
    private readonly mapper: TicketMapperService,
    private readonly documentApi: DocumentApiService,
    private readonly fieldUpdate: TicketFieldUpdateService,
  ) { }

  getDetail(recordId: string): Observable<Ticket | null> {
    return forkJoin({
      detail: this.api.getTicketDetail(recordId),
      docs: this.searchDocs(recordId, AttachmentType.ADJUNTOS),
      clientDocs: this.searchDocs(recordId, AttachmentType.RESPUESTA_USUARIO),
    }).pipe(
      map(({ detail, docs, clientDocs }) => (detail?.ticketRecordId
        ? {
          ...this.mapper.mapTicketDetail(detail, 'client'),
          attachedDocument: this.mapper.mapTicketDocuments(docs),
          clientAttachedDocument: this.mapper.mapTicketDocuments(clientDocs),
        }
        : null)),
    );
  }

  private searchDocs(recordId: string, attachmentType: AttachmentType) {
    return this.documentApi.searchDocuments({ domainType: DOMAIN_TYPE_SUPPORT, recordId, attachmentType });
  }

  getByConstitution(constitutionRecordId: string): Observable<TicketGetResponse[]> {
    return this.api.findAllTickets({ recordIdConstitution: [constitutionRecordId] }).pipe(
      map(records => records ?? []),
    );
  }

  register(payload: TicketClientRegisterPayload): Observable<{ recordIdTicket: string }> {
    return this.api.createTicket({
      recordIdConstitucion: payload.constitutionRecordId,
      tipoIncidencia: [this.mapper.reverseMapIncidentType(payload.incidentType)],
      descripcion: payload.description,
      categoriaTicket: TicketCategory.TO_CLIENT,
      actorCreador: ActorType.NOTARY,
    }).pipe(
      map(response => ({ recordIdTicket: response.ticketRecordId })),
    );
  }

  updateStatus(recordId: string, newStatus: string, actorType: string, reportedBy: string): Observable<void> {
    return this.api.updateTicketStatus(recordId, {
      newStatus,
      reportedBy,
      actorType,
      actionType: 'STATUS_CHANGE',
    }).pipe(map(() => undefined));
  }

  updateStringField(recordId: string, field: string, value: any): Observable<void> {
    return this.fieldUpdate.updateField(recordId, field, value);
  }

  answerUser(recordId: string, answer: string, currentStatus: string): Observable<void> {
    // El texto se guarda como campo; solo un ticket nuevo (OPEN) pasa a IN_PROGRESS por /status.
    const save$ = this.fieldUpdate.updateField(recordId, 'respuestaUsuario', answer);
    if (currentStatus !== 'OPEN') return save$;
    return save$.pipe(
      switchMap(() => this.api.updateTicketStatus(recordId, {
        newStatus: 'IN_PROGRESS',
        reportedBy: 'USER',
        actorType: 'USER',
        actionType: 'ANSWER_BY_CLIENT',
      })),
      map(() => undefined),
    );
  }

}
